import { useEffect, useState } from "react";
import api from "../lib/api";
import { setCurrentLanguage, loadUiStrings, useLanguage } from "../lib/i18n";

/** Wave S — Multi-Language AI language picker.
 *  Shows all 22 supported languages grouped by tier with native names,
 *  RTL flag, and a one-line tier description. Persists via
 *  PUT /api/users/{id}/language. */
export default function LanguageSettings({ open, onClose }) {
  const current = useLanguage();
  const [languages, setLanguages] = useState([]);
  const [picked, setPicked] = useState(current || "en");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    api.get("/language/list")
        .then(r => setLanguages(r.data.languages || []))
        .catch(e => setError(String(e?.response?.data?.detail || e)));
  }, [open]);

  useEffect(() => { setPicked(current || "en"); }, [current, open]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const me = await api.get("/auth/me");
      const userId = me.data?.id;
      if (!userId) throw new Error("Not signed in");
      await api.put(`/users/${userId}/language`, { language: picked });
      setCurrentLanguage(picked);
      await loadUiStrings(picked);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    }
    setSaving(false);
  };

  if (!open) return null;

  const tier1 = languages.filter(l => l.tier === 1);
  const tier2 = languages.filter(l => l.tier === 2);
  const tier3 = languages.filter(l => l.tier === 3);

  return (
    <div data-testid="language-settings" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        zIndex: 1000, display: "flex", alignItems: "center",
        justifyContent: "center", padding: 32 }}>
      <div style={{ background: "var(--pf-card)",
                          border: "1px solid var(--pf-border)",
                          borderRadius: 8, padding: 24, maxWidth: 720,
                          width: "100%", maxHeight: "85vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#F26B1F" }}>
              Language preferences
            </div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)",
                                marginTop: 2 }}>
              UI displays in your chosen language. Engineering outputs (tags,
              codes, drawings) stay in English. Prompts work in any language.
            </div>
          </div>
          <button data-testid="language-close" style={btnGhost}
              onClick={onClose}>✕</button>
        </div>

        {error && <div style={{ marginTop: 12, color: "#EF4444",
                                                fontSize: 11 }}>⚠ {error}</div>}

        <div style={{ marginTop: 16 }}>
          <TierBlock title="Tier 1 — Full support (prompts · UI · reports)"
                            languages={tier1} picked={picked} setPicked={setPicked} />
          <TierBlock title="Tier 2 — Prompts + UI"
                            languages={tier2} picked={picked} setPicked={setPicked} />
          <TierBlock title="Tier 3 — Prompts only"
                            languages={tier3} picked={picked} setPicked={setPicked} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end",
                            gap: 8, marginTop: 18 }}>
          <button data-testid="language-cancel"
              onClick={onClose} style={btnGhost}>Cancel</button>
          <button data-testid="language-save"
              onClick={save} disabled={saving} style={btn}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TierBlock({ title, languages, picked, setPicked }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "var(--pf-muted)",
                          textTransform: "uppercase",
                          letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                          gap: 6 }}>
        {languages.map(l => {
          const isPicked = picked === l.code;
          return (
            <button key={l.code}
                data-testid={`lang-pick-${l.code}`}
                onClick={() => setPicked(l.code)}
                style={{
                  padding: "8px 10px",
                  background: isPicked ? "#F26B1F" : "var(--pf-bg)",
                  color: isPicked ? "white" : "var(--pf-text)",
                  border: `1px solid ${isPicked ? "#F26B1F" : "var(--pf-border)"}`,
                  borderRadius: 4, fontSize: 12, cursor: "pointer",
                  textAlign: "left", display: "flex",
                  flexDirection: "column", gap: 2,
                }}>
              <span style={{ fontWeight: 600,
                                    direction: l.rtl ? "rtl" : "ltr" }}>
                {l.native_name}
                {l.rtl && (
                  <span style={{ marginLeft: 6, fontSize: 8,
                                          opacity: 0.6, fontWeight: 400 }}>RTL</span>
                )}
              </span>
              <span style={{ fontSize: 9,
                                    opacity: isPicked ? 0.85 : 0.6 }}>
                {l.english_name} · {l.code}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const btn = { background: "#F26B1F", color: "white", border: "none",
                  padding: "6px 16px", borderRadius: 4, fontSize: 12,
                  cursor: "pointer" };
const btnGhost = { ...btn, background: "transparent",
                          color: "var(--pf-text)",
                          border: "1px solid var(--pf-border)" };
